import { type TriagemDecisao, type TriagemDecisaoLote } from '../types';

type TriagemDecisaoApi = 'APROVADA' | 'NEGADA' | 'REGULARIZACAO';

const DECISAO_API: Record<TriagemDecisao['decisao'], TriagemDecisaoApi> = {
  APROVAR: 'APROVADA',
  NEGAR: 'NEGADA',
  PEDIR_CORRECAO: 'REGULARIZACAO',
};

export interface TriagemDecisaoApiBody {
  decisao: TriagemDecisaoApi;
  flagDespejo?: TriagemDecisao['flagDespejo'];
  motivoNegacao?: TriagemDecisao['motivoNegacao'];
  motivoNegacaoTexto?: string;
  observacaoSecretaria?: string;
  justificativaItensReprovados?: string;
  pendencias?: TriagemDecisao['pendencias'];
}

/**
 * Os três ramos do core são `.strict()`: a justificativa só existe na APROVADA, e mandá-la
 * em outro ramo é 400. Por isso o filtro por tipo, e não só pela presença do texto. O
 * `flagDespejo` existe na APROVADA e na REGULARIZACAO (obrigatório nas duas: core#2160 e
 * item 8 / D-02) e nunca na NEGADA — quem garante é o `paraTriagemDecisao`, que só o põe
 * nesses dois ramos.
 */
function justificativaDaAprovacao(
  tipo: TriagemDecisao['decisao'],
  justificativa: string | undefined,
): Pick<TriagemDecisaoApiBody, 'justificativaItensReprovados'> {
  const texto = justificativa?.trim();
  return tipo === 'APROVAR' && texto ? { justificativaItensReprovados: texto } : {};
}

export function toTriagemDecisaoApiBody(decisao: TriagemDecisao): TriagemDecisaoApiBody {
  const {
    decisao: tipo,
    textoMotivoNegacao,
    flagDespejo,
    motivoNegacao,
    observacaoSecretaria,
    justificativaItensReprovados,
    pendencias,
  } = decisao;
  return {
    decisao: DECISAO_API[tipo],
    ...(flagDespejo !== undefined ? { flagDespejo } : {}),
    ...(motivoNegacao !== undefined ? { motivoNegacao } : {}),
    ...(textoMotivoNegacao ? { motivoNegacaoTexto: textoMotivoNegacao } : {}),
    ...(observacaoSecretaria ? { observacaoSecretaria } : {}),
    ...justificativaDaAprovacao(tipo, justificativaItensReprovados),
    ...(pendencias !== undefined ? { pendencias } : {}),
  };
}

interface TriagemDecisaoLoteApiBody {
  decisao: TriagemDecisaoApi;
  flagDespejoPorProcesso?: Record<string, boolean>;
  motivoNegacao?: string;
  motivoNegacaoTexto?: string;
  prazoDiasUteis?: number;
  observacaoSecretaria?: string;
  justificativaItensReprovados?: string;
}

/**
 * O mapa de despejo recortado pelos `ids` (core#2160): só quem está neles, e só quem tem Sim ou
 * Não. Quem ficou sem escolha fica FORA, e não `false` — `null` não vira "não é despejo".
 *
 * @example recortarDespejoPorProcessos({ a: true, b: false }, ['a', 'c']) // { a: true }
 */
export function recortarDespejoPorProcessos(
  mapa: Record<string, boolean>,
  ids: readonly string[],
): Record<string, boolean> {
  const recorte: Record<string, boolean> = {};
  for (const id of ids) {
    const valor = mapa[id];
    if (typeof valor === 'boolean') recorte[id] = valor;
  }
  return recorte;
}

/**
 * O lote sai em levas de `LOTE_TRIAGEM_MAX` (`executarLoteEmLevas`), e o core recusa com 400 a
 * leva inteira se o mapa trouxer um processo que não está nela — mandar o mapa do lote inteiro
 * derrubaria todas as levas a partir da segunda que tiver mais de 100 processos marcados.
 *
 * Aprovar e pedir ajustes recortam o mapa (aprovação no core#2160, ajustes no item 8 / D-02); quem
 * garante que ele exista, mesmo vazio, é o `paraTriagemDecisaoLote`. Negar não julga despejo — o
 * ramo do core é `.strict()` e recusaria a chave.
 */
function despejoDaLeva(
  tipo: TriagemDecisaoLote['decisao'],
  mapa: Record<string, boolean> | undefined,
  processoIds: string[],
): Pick<TriagemDecisaoLoteApiBody, 'flagDespejoPorProcesso'> {
  if (tipo === 'NEGAR' || !mapa) return {};
  return { flagDespejoPorProcesso: recortarDespejoPorProcessos(mapa, processoIds) };
}

/**
 * @example
 * toTriagemDecisaoLoteApiBody(
 *   { decisao: 'APROVAR', flagDespejoPorProcesso: { p1: true, p2: false } },
 *   ['p1'],
 * ); // { decisao: 'APROVADA', flagDespejoPorProcesso: { p1: true } }
 */
export function toTriagemDecisaoLoteApiBody(
  decisao: TriagemDecisaoLote,
  processoIds: string[],
): TriagemDecisaoLoteApiBody {
  const {
    decisao: tipo,
    textoMotivoNegacao,
    flagDespejoPorProcesso,
    motivoNegacao,
    prazoDiasUteis,
    observacaoSecretaria,
    justificativaItensReprovados,
  } = decisao;
  const ehRegularizacao = tipo === 'PEDIR_CORRECAO';
  return {
    decisao: DECISAO_API[tipo],
    ...despejoDaLeva(tipo, flagDespejoPorProcesso, processoIds),
    ...(motivoNegacao !== undefined && tipo === 'NEGAR' ? { motivoNegacao } : {}),
    ...(textoMotivoNegacao && tipo === 'NEGAR' ? { motivoNegacaoTexto: textoMotivoNegacao } : {}),
    ...(ehRegularizacao && prazoDiasUteis !== undefined ? { prazoDiasUteis } : {}),
    ...(observacaoSecretaria ? { observacaoSecretaria } : {}),
    ...justificativaDaAprovacao(tipo, justificativaItensReprovados),
  };
}
